import React from "react";
import ReactECharts from "src/utils/EChartsReact";
import { getCommonTooltipConfig, buildChartTooltip, useTooltipCoordination } from "../tooltipFormatter";
import { hasAnyStatMarkers, resolveStatMarkerRows, buildVClusteredMarkPoints } from "./../../components/StatMarkerUtils";
import { formatNumber, convertStringToNumeric } from "src/pages/Insights/transformer/dashboardUtils";
import { chartConfig } from "../../chartConfig";
import { IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig } from "../../ChartScaleContext";
import { getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_PLACEHOLDER, SUPPRESSED_VALUE_COLOR } from "../chartHelpers";

interface IMiniVBarChartProps {
	series: {
		name: string;
		value: number;
		n_size: number | string;
		stat_marker?: IStatMarker | null;
	}[];
	waves: Record<string, string>;
	width: number;
	height?: number;
	labelFormat: string;
	yAxisDomain: { min: number; max: number };
	top: number;
	bottom?: number;
	barWidth?: number;
	dataLabelFontSize?: number;
	isUltraCompact?: boolean;
	statTestConfig?: IStatTestConfig | null;
}

const MiniVBarChart: React.FC<IMiniVBarChartProps> = ({
	series,
	waves,
	width,
	height = 280,
	labelFormat,
	yAxisDomain,
	top,
	bottom = 0,
	barWidth,
	dataLabelFontSize,
	isUltraCompact = false,
	statTestConfig,
}) => {
	const typographyConfig = chartConfig.getTypographyConfig();
	const colorsConfig = chartConfig.getColorsConfig();
	const vClusteredConfig = chartConfig.getVerticalClusteredConfig();
	const scaled = useScaledConfig();
	const labelFontSize = React.useMemo(() => dataLabelFontSize ?? typographyConfig.fontSize.dataLabel, [dataLabelFontSize, typographyConfig.fontSize.dataLabel]);
	const hasAnyStatMarker = React.useMemo(() => hasAnyStatMarkers(series, statTestConfig), [series, statTestConfig]);
	const statMarkerLayout = React.useMemo(() => vClusteredConfig.statMarkerLayout, [vClusteredConfig]);
	const { chartRef, onEvents } = useTooltipCoordination();

	// Calculate effective bar width and category gap
	const { effectiveBarWidth, categoryGap } = React.useMemo(() => {
		const scaledMinWidth = Math.max(
			vClusteredConfig.minBarWidth * scaled.scaleFactor,
			6, // hard floor safety
		);

		const scaledMaxWidth =
			series.length === 1 ? Math.min(vClusteredConfig.maxBarWidthSingle, scaled.geometry.maxBarWidth) : Math.min(vClusteredConfig.maxBarWidth, scaled.geometry.maxBarWidth);
		const padding = 12;
		const availableWidth = width - padding;

		const spacingMultiplier = series.length === 1 ? 0.7 : 0.9;

		let calculatedWidth = Math.floor((availableWidth / series.length) * spacingMultiplier);

		if (barWidth) {
			calculatedWidth = Math.max(calculatedWidth, barWidth);
		}

		const finalBarWidth = Math.max(scaledMinWidth, Math.min(calculatedWidth, scaledMaxWidth));

		const totalBarsWidth = series.length * finalBarWidth;
		const remainingSpace = Math.max(0, width - totalBarsWidth);
		const gap = remainingSpace / width;

		return {
			effectiveBarWidth: finalBarWidth,
			categoryGap: `${(gap * 100).toFixed(1)}%`,
		};
	}, [barWidth, width, series.length, vClusteredConfig, scaled.scaleFactor, scaled.geometry.maxBarWidth]);

	// Tooltip formatter with stat marker hover support
	const tooltipFormatter = React.useCallback(
		(params: any) => buildChartTooltip(params, series, labelFormat, statTestConfig, waves),
		[series, labelFormat, statTestConfig, waves],
	);

	const adjustedTop = React.useMemo(() => {
		if (hasAnyStatMarker && statMarkerLayout) {
			// Extra space needed: marker font size + gap between marker and data label
			const markerOverhead = 12;
			return top + Math.max(statMarkerLayout.gridPadding, markerOverhead);
		}
		return top;
	}, [hasAnyStatMarker, top, statMarkerLayout]);

	const option = React.useMemo(
		() => ({
			animation: false,
			grid: {
				left: 0,
				right: 0,
				top: adjustedTop,
				bottom,
				containLabel: false,
			},
			tooltip: {
				...getCommonTooltipConfig(),
				formatter: tooltipFormatter,
				renderMode: "html",
				className: "stat-marker-tooltip",
			},
			xAxis: {
				type: "category",
				data: [""],
				show: false,
				axisLine: { show: false },
				axisTick: { show: false },
			},
			yAxis: {
				type: "value",
				min: yAxisDomain.min,
				max: yAxisDomain.max,
				show: false,
				splitLine: { show: false },
				axisLine: {
					show: true,
					onZero: true,
					lineStyle: {
						color: colorsConfig.borders.gray,
						width: scaled.geometry.strokeWidth,
					},
				},
			},
			series: series.map((s, index) => {
				const isNoDataValue =
					s.value === undefined ||
					s.value === null ||
					(s.value as unknown) === "No Data" ||
					(s.value as unknown) === "No data" ||
					(typeof s.value === "number" && !Number.isFinite(s.value));

				const nSizeStatusLabel = getNSizeStatusLabel(s.n_size);
				const isNoDataNSize = nSizeStatusLabel === NSizeLabelText.NoData;
				const suppressValue = nSizeStatusLabel !== null;

				const formattedVal = isNoDataValue || suppressValue ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : formatNumber(s.value, labelFormat);
				const value = isNoDataValue || suppressValue ? 0 : convertStringToNumeric(formattedVal);

				const isSuppressed = isNoDataValue || suppressValue;
				const displayLabel = isSuppressed
					? (nSizeStatusLabel === NSizeLabelText.LowN ? NSizeLabelText.LowN : SUPPRESSED_VALUE_PLACEHOLDER)
					: value === 0 ? (labelFormat.endsWith("%") ? "0%" : "0") : labelFormat.endsWith("%") ? `${value}%` : value;

				// Resolve stat markers
				const resolvedRows = !isNoDataValue && !suppressValue && statTestConfig ? resolveStatMarkerRows(s.stat_marker, statTestConfig) : [];

				// Pass actual bar width and label font size for centered positioning
				const markPointData = resolvedRows.length > 0 ? buildVClusteredMarkPoints(resolvedRows, statTestConfig!, labelFontSize, effectiveBarWidth) : [];

				markPointData.forEach((pt: any) => {
					pt.dataPointMeta = {
						stat_marker: s.stat_marker ?? null,
						value: s.value,
						n_size: s.n_size,
						name: s.name,
					};
				});

				return {
					name: s.name,
					type: "bar",
					data: [
						{
							value,
							n_size: isNoDataNSize ? NSizeLabelText.NoData : s.n_size,
							name: s.name,
							displayValue: isNoDataValue || suppressValue ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : formattedVal,
							stat_marker: s.stat_marker ?? null,
						},
					],
					itemStyle: {
						color: waves[s.name],
					},
					label: {
						show: true,
						position: Number(value) < 0 ? "bottom" : "top",
						fontSize: displayLabel === NSizeLabelText.LowN ? 8 : labelFontSize,
						fontWeight: "normal" as const,
						formatter: () => displayLabel,
						...(isSuppressed ? { color: SUPPRESSED_VALUE_COLOR } : {}),
						...(displayLabel === NSizeLabelText.LowN
							? { rotate: 90, align: "center", verticalAlign: "middle", offset: [effectiveBarWidth / 2, 0] }
							: {}),
					},
					barWidth: effectiveBarWidth,
					barGap: "0%",
					barCategoryGap: index === 0 ? categoryGap : "0%",
					...(markPointData.length > 0
						? {
							markPoint: {
								symbol: "circle",
								symbolSize: 0,
								data: markPointData,
								animation: false,
								tooltip: { show: true },
							},
						}
						: {}),
				};
			}),
		}),
		[
			series,
			waves,
			adjustedTop,
			bottom,
			labelFormat,
			yAxisDomain,
			colorsConfig.borders.gray,
			labelFontSize,
			effectiveBarWidth,
			categoryGap,
			statTestConfig,
			hasAnyStatMarker,
			tooltipFormatter,
		],
	);

	return <ReactECharts ref={chartRef} onEvents={onEvents} option={option} style={{ width: "100%", height }} autoResize opts={{ renderer: "svg" }} />;
};

export default React.memo(MiniVBarChart);
